import { ConfigService } from '@maia/config';
import { BaseService } from '@maia/feature-utils';
import { HttpService } from '@maia/httpClient';
import {
  ICompositionEntity,
  IGetMutationCartEntity,
  IPreconditionQuestion,
  IProductEntity,
  PutQuestionDto,
} from '@maia/shared';

export class MutationService extends BaseService {
  constructor(httpService: HttpService, configService: ConfigService) {
    super(httpService, configService);
  }

  public async createMutation({
    customerGuid,
    insuranceGuid,
  }: {
    customerGuid: string;
    insuranceGuid: string;
  }) {
    return await this.post<{ cart_guid: string }, {}>(
      `customers/${customerGuid}/insurances/${insuranceGuid}/mutations`,
      {},
      'create mutation',
    );
  }

  public async getMutationCart({
    customerGuid,
    cartGuid,
  }: {
    customerGuid: string;
    cartGuid: string;
  }) {
    return await this.get<IGetMutationCartEntity>(
      `customers/${customerGuid}/mutations/${cartGuid}`,
      'get mutation cart',
    );
  }

  public async getPreconditionQuestions({
    customerGuid,
    cartGuid,
  }: {
    customerGuid: string;
    cartGuid: string;
  }) {
    return await this.get<Array<IPreconditionQuestion>>(
      `customers/${customerGuid}/mutations/${cartGuid}/questions`,
      'get mutation precondition questions',
    );
  }

  public async putPreconditionQuestions({
    customerGuid,
    cartGuid,
    questions,
  }: {
    customerGuid: string;
    cartGuid: string;
    questions: ConstructorParameters<typeof PutQuestionDto>[number];
  }) {
    return await this.put<unknown, PutQuestionDto['questions']>(
      `customers/${customerGuid}/mutations/${cartGuid}/questions`,
      {
        questions,
      },
      'Answer precondition questions',
    );
  }

  public async putCompositions({
    customerGuid,
    cartGuid,
    compositionGuid,
  }: {
    customerGuid: string;
    cartGuid: string;
    compositionGuid: string;
  }) {
    return await this.put<{ selected: boolean }, {}>(
      `customers/${customerGuid}/mutations/${cartGuid}/compositions/${compositionGuid}`,
      {},
      'Put compositions',
    );
  }

  public async getCompositions({
    customerGuid,
    cartGuid,
  }: {
    customerGuid: string;
    cartGuid: string;
  }) {
    return await this.get<Array<ICompositionEntity>>(
      `customers/${customerGuid}/mutations/${cartGuid}/compositions`,
      'Get compositions',
    );
  }

  public async getProducts({
    customerGuid,
    cartGuid,
  }: {
    customerGuid: string;
    cartGuid: string;
  }) {
    return await this.get<Array<IProductEntity>>(
      `customers/${customerGuid}/mutations/${cartGuid}/products`,
      'Get product question',
    );
  }

  public async putProductQuestion({
    customerGuid,
    cartGuid,
    productGuid,
    questions,
  }: {
    customerGuid: string;
    cartGuid: string;
    productGuid: string;
    questions: ConstructorParameters<typeof PutQuestionDto>[number];
  }) {
    return await this.put<unknown, Array<{ guid: string; answer: string }>>(
      `customers/${customerGuid}/mutations/${cartGuid}/products/${productGuid}/questions`,
      questions,
      'Put product question',
    );
  }

  public async submitMutation({
    customerGuid,
    cartGuid,
    effectiveDate,
  }: {
    customerGuid: string;
    cartGuid: string;
    effectiveDate: string;
  }) {
    return await this.put<unknown, { effective_date: string }>(
      `customers/${customerGuid}/mutations/${cartGuid}/finalize`,
      { effective_date: effectiveDate },
      'Submit',
    );
  }
}
